import useTranslation from '../../useTranslation';
import { SharedComponent } from '../../../enums';
import type { SurveyProperties } from '@aurora/shared-generated/types/graphql-schema-types';
import type { I18n } from '@aurora/shared-types/texts';

type SurveyQuestionsAndAnswersText<T, R> = {
  /**
   * The i18n object used to format messages.
   */
  i18n: I18n<T, R>;
  /**
   * Get the message key for a question.
   *
   * @param questionId The ID of the question.
   */
  getQuestionTextKeyId(questionId: string): string;
  /**
   * Get the message key for an answer.
   *
   * @param questionId The ID of the question.
   * @param answerId The ID of the answer.
   */
  getAnswerTextKeyId(questionId: string, answerId: string): string;
  /**
   * Format a message for a question.
   *
   * @param questionId The ID of the question.
   * @param surveyProperties The survey properties to use as the context of the formatted message
   */
  formatMessageForQuestion(
    questionId: string,
    surveyProperties: Pick<SurveyProperties, 'surveyBrandName' | 'surveyCommunityName'>
  ): string;
  /**
   * Format a message for an answer.
   *
   * @param answerId The ID of the answer.
   * @param questionId The ID of the question.
   * @param surveyProperties The survey properties to use as the context of the formatted message
   */
  formatMessageForAnswer(
    answerId: string,
    questionId: string,
    surveyProperties: Pick<SurveyProperties, 'surveyBrandName' | 'surveyCommunityName'>
  ): string;
};

/**
 * Hook to format survey questions and answers text.
 *
 * @author Adam Ayres
 */
export default function useSurveyQuestionsAndAnswersText<T, R>(): SurveyQuestionsAndAnswersText<
  T,
  R
> {
  const i18n = useTranslation(SharedComponent.SURVEY_QUESTIONS_AND_ANSWERS);
  const { formatMessage } = i18n;

  function internalFormatMessage(
    key: string,
    surveyProperties: Pick<SurveyProperties, 'surveyBrandName' | 'surveyCommunityName'>
  ): string {
    return formatMessage(key, {
      communityName: surveyProperties.surveyCommunityName ?? 'Khoros',
      brandName: surveyProperties.surveyBrandName ?? 'ABC Company'
    });
  }

  // TODO: Move to Helper; DRY
  function getTruncatedQuestionId(questionId: string): string {
    return questionId.split('.').at(-1);
  }

  function getQuestionTextKeyId(questionId: string): string {
    return `${getTruncatedQuestionId(questionId)}.label`;
  }

  function getAnswerTextKeyId(questionId: string, answerId: string): string {
    return `${getTruncatedQuestionId(questionId)}.${answerId}.label`;
  }

  return {
    getQuestionTextKeyId,
    getAnswerTextKeyId,
    formatMessageForQuestion: (
      questionId: string,
      surveyProperties: Pick<SurveyProperties, 'surveyBrandName' | 'surveyCommunityName'>
    ): string => {
      const key = getQuestionTextKeyId(questionId);
      return internalFormatMessage(key, surveyProperties);
    },
    formatMessageForAnswer(
      questionId: string,
      answerId: string,
      surveyProperties: Pick<SurveyProperties, 'surveyBrandName' | 'surveyCommunityName'>
    ): string {
      const key = getAnswerTextKeyId(questionId, answerId);
      return internalFormatMessage(key, surveyProperties);
    },
    i18n
  };
}
